"use client";

import type { ProductDetails } from "@ellaandil/formatters";
import { parseAsString, useQueryState } from "nuqs";
import { useEffect } from "react";
import { useDPC } from "@/lib/depict/dpc";
import { getDepictVariantId } from "@/lib/depict/product-id";

/**
 * Fires Depict's product page view tracking. DPC ties the next click/event to
 * this product context, which Depict uses to attribute downstream revenue and
 * power recommendations.
 *
 * Depict's `product_id` is variant-level for ellaandil (e.g. `EITE06103xs`),
 * so we wait until SizeSelector has auto-picked the first available size
 * (mirrored to `?size=` in the URL) before emitting. Re-fires when the user
 * switches size.
 *
 * See https://docs.depict.ai/api-guide/tracking/product-pages
 */
export const DepictView = ({ items }: { items: ProductDetails["items"] }) => {
	const { data: dpc } = useDPC();
	const [size] = useQueryState("size", parseAsString);
	const productId = getDepictVariantId(items, size);

	useEffect(() => {
		if (!dpc || !productId) return;
		dpc.setProductId(productId);
	}, [dpc, productId]);

	return null;
};
